<template>
  <div ref="catalogRoot" class="container">
    <Breadcrumb :links="pageBreadcrumbs"/>
    <ServiceFilter v-model="filters" :filters="clinicData?.filters" :title="pageTitle"
                   :description="getServiceDescription" @updateOnMobile="updateOnMobile"
                   @updateFilters="onUpdateFilters"/>
    <ServiceCategoriesList v-if="categories && categories.length" :filters="clinicQuery" :items="categories"
                           :current-name="currentServiceName" keep-order/>
    <ServiceCategoriesList v-if="childTiles.length > 1" :filters="clinicQuery" :items="childTiles"
                           :current-name="currentServiceName" keep-order/>
    <NuxtLink v-else-if="childTiles.length === 1" :to="`/uslugi/${childTiles[0].slug}/`"
              class="service-child-link">
      {{ childTiles[0].name }}
      <span v-if="childTiles[0].clinicsCount !== undefined" class="service-child-link__count">
        {{ childTiles[0].clinicsCount }} {{ getPlural(childTiles[0].clinicsCount, ['клиника', 'клиники', 'клиник']) }}
      </span>
    </NuxtLink>
    <ServiceAnimalFilter :options="animalOptions" :landings="animalLandings" :fixed="isAnimalLanding"
                         :parent-slug="catalogParent?.slug" :city="!!filters.city"
                         @select="onAnimalSelect"/>
    <ClinicListBefore
        v-model:view-type="viewType"
        :clinic-count="clinicCount"
    />
    <ClinicMap v-if="viewType === 'map' && !isEmptyResults" :filters="clinicQuery" :view="viewType === 'map'"
               :serviceId="currentServiceId"/>
    <div v-show="viewType === 'list' || isEmptyResults" ref="clinicList">
      <UIListSkeleton v-if="isClinicsLoading" variant="service"/>
      <div v-else-if="clinicError || serviceError" class="clinics-error" role="alert">
        <p>Не удалось загрузить клиники.</p>
        <Button is-button text="Повторить" @click="onRetryListing()"/>
      </div>
      <div v-else-if="clinics.length" class="clinics">
        <ClinicServiceCard v-for="clinic in clinics" :key="clinic.id" :clinic="clinic" :serviceId="currentServiceId"
                           :nodeSlug="catalogNode?.slug"/>
      </div>
      <ClinicCatalogEmptyState v-else :clinics-url="allClinicsUrl"
                               :title="selectedAnimalLabel ? `Для ${selectedAnimalLabel} предложений пока нет` : undefined"
                               description="Попробуйте другое название услуги или уберите часть фильтров. Ещё можно выбрать другую услугу в каталоге."
                               @search="focusSearch">
        <template v-if="selectedAnimalLabel" #action>
          <NuxtLink v-if="isAnimalLanding && catalogParent" :to="`/uslugi/${catalogParent.slug}/`"
                    class="catalog-empty-state__reset">
            Показать все виды
          </NuxtLink>
          <button v-else type="button" class="catalog-empty-state__reset" @click="onAnimalSelect(defaultAnimal)">
            Показать все виды
          </button>
        </template>
      </ClinicCatalogEmptyState>
      <Pagination
        v-if="!isClinicsLoading && !clinicError && !serviceError && meta"
        :meta="meta"
        :page-href="paginationHref"
        @page-changed="onPageChanged"
      />
      <UICollapsedText class="vet-list-text" v-if="clinicData?.seo?.fullText && page === 1"
                       :content="clinicData?.seo?.fullText"
                       :lines="8" :withoutEllipsis="true"/>
    </div>
    <!-- Дочерние узлы показаны плитками наверху страницы: второй список тех же ссылок внизу
         был бы дублем. Здесь остаётся только путь назад — родитель (ТЗ §7.8). -->
    <section v-if="catalogParent" class="service-related">
      <h2 class="service-related__title">Другие услуги раздела</h2>
      <div class="service-related__list">
        <NuxtLink :to="`/uslugi/${catalogParent.slug}/`" class="service-related__item">
          <SvgoSymbolPets class="service-related__icon" aria-hidden="true"/>
          {{ catalogParent.nameSeo || catalogParent.name }}
        </NuxtLink>
      </div>
    </section>
    <section v-if="clinicData?.seo?.relatedServices?.length && page === 1" class="service-related">
      <h2 class="service-related__title">Смежные услуги</h2>
      <div class="service-related__list">
        <NuxtLink v-for="svc in clinicData.seo.relatedServices" :key="svc.id"
                  :to="getRelatedServiceUrl(svc.slug)"
                  class="service-related__item">
          <SvgoSymbolPets class="service-related__icon" aria-hidden="true"/>
          {{ svc.name }}
        </NuxtLink>
      </div>
    </section>
    <FaqSection v-if="clinicData?.seo?.faqs?.length && page === 1" :items="clinicData.seo.faqs" style="margin-bottom: 0;"/>
  </div>
</template>

<script lang="ts" setup>
import type {AnimalOption} from '~/components/Service/AnimalFilter.vue';
import type {ServiceCategory, TClinic, TClinicFilter, TClinicRaw, TServiceResponse} from "~/types/clinic";
import type {Meta} from "~/types/request/meta";
import {computed, ref} from "vue";
import {updateFiltersData} from "~/utils/doctorFilters";
import {getPlural} from "~/utils/helpers";
import {
  clearGeoCatalogCityFilterCleared,
  isGeoCatalogCityFilterCleared,
  markGeoCatalogCityFilterCleared,
} from '~/utils/geoCatalog';
import type {FilterSeo} from "~/types/filterSeo";
import {useCityStore} from '~/stores/city';
import {omitClinicPhoneFromList} from "~/utils/omitClinicPhone";

const router = useRouter();
const route = useRoute();
const nuxtApp = useNuxtApp();
const cityStore = useCityStore();
const {trackPageView} = usePageView();

// Трекинг просмотра страницы каталога услуг
onMounted(() => {
  const segment = route.params.segment ? String(route.params.segment) : undefined;
  const service = route.params.service ? String(route.params.service) : undefined;
  const animal = route.params.animal ? String(route.params.animal) : undefined;
  const slug = [segment, service, animal].filter(Boolean).join('/') || undefined;
  trackPageView('services_catalog', slug);
  void syncConfirmedCityFromStore();
});

const defaultAnimal = 'zhivotnyh';
const serviceCatalogAnimalSlugPattern = /^[a-z0-9_-]+$/;

function normalizeServiceCatalogAnimalSlug(slug: string): string {
  if (!slug || !serviceCatalogAnimalSlugPattern.test(slug)) {
    return defaultAnimal;
  }

  return slug;
}
const page = ref<number>(Number(route.query.page) || 1);
const loading = ref(false);
const viewType = ref<'list' | 'map'>(route.query.view === 'map' ? 'map' : 'list');
const clinicList = ref<HTMLElement | null>(null);
const shouldScrollOnPageChange = ref(false);
const filters = ref<{
  city: string;
  service: string;
  priceRow: string;
  animal: string;
  search: string;
  isAlwaysOpen: boolean;
}>({
  city: '',
  service: '',
  priceRow: '',
  animal: defaultAnimal,
  search: '',
  isAlwaysOpen: false
});

function paramToString(p: string | string[] | undefined): string {
  if (p === undefined || p === null) return '';
  return Array.isArray(p) ? (p[0] ?? '').toString() : String(p);
}

const {apiQuery} = useApi();

const {data: segmentKind, pending: segmentResolvePending} = await useAsyncData<{
  city: string;
  service: string
} | null>(
    `uslugi-segment-${route.fullPath}`,
    async () => {
      const seg = paramToString(route.params.segment);
      const svc = paramToString(route.params.service);
      // Городской адрес узла однозначен по позиции сегментов — пробовать нечего.
      if (!seg || svc || paramToString(route.params.node)) {
        return {city: '', service: ''};
      }
      try {
        await apiQuery(`public/services/${seg}`, 'GET');
        return {city: '', service: seg};
      } catch {
        return {city: seg, service: ''};
      }
    },
    {
      watch: [() => route.fullPath],
    },
);

const ambiguousReady = computed(() => {
  const seg = paramToString(route.params.segment);
  const svc = paramToString(route.params.service);
  if (!seg || svc || paramToString(route.params.node)) {
    return true;
  }
  return !segmentResolvePending.value && segmentKind.value != null;
});

function applyRouteToFilters(): void {
  const seg = paramToString(route.params.segment);
  const svc = paramToString(route.params.service);
  const anim = paramToString(route.params.animal);
  const node = paramToString(route.params.node);
  const queryService = paramToString(route.query.service as string | string[] | undefined);
  const queryAnimal = paramToString(route.query.animal as string | string[] | undefined);
  const querySearch = paramToString(route.query.search as string | string[] | undefined);

  filters.value.priceRow = paramToString(route.query.priceRow as string | string[] | undefined);

  if (seg && node) {
    // `/uslugi/{город}/{узел}/`: оба сегмента известны по позиции, и пробный запрос,
    // различающий город и услугу в плоском адресе, здесь не нужен.
    filters.value.city = seg;
    filters.value.service = node;
    filters.value.animal = normalizeServiceCatalogAnimalSlug(queryAnimal);
  } else if (seg && svc && anim) {
    filters.value.city = seg;
    filters.value.service = svc;
    filters.value.animal = normalizeServiceCatalogAnimalSlug(anim);
  } else if (seg && !svc) {
    const resolved = segmentKind.value;
    if (!resolved) {
      filters.value.city = '';
      filters.value.service = '';
      filters.value.animal = defaultAnimal;
      filters.value.isAlwaysOpen = route.query.isAlwaysOpen === 'y';
      filters.value.search = querySearch;
      return;
    }
    filters.value.city = resolved.city;
    filters.value.service = resolved.service;
    filters.value.animal = normalizeServiceCatalogAnimalSlug(queryAnimal);
  } else {
    filters.value.city = '';
    filters.value.service = queryService;
    filters.value.animal = normalizeServiceCatalogAnimalSlug(queryAnimal);
  }

  filters.value.isAlwaysOpen = route.query.isAlwaysOpen === 'y';
  filters.value.search = querySearch;
}

applyRouteToFilters();

/**
 * Город именно из адреса, а не из фильтров: фильтр города живёт и на плоском узле, а вердикт
 * публикации спрашивается только для страницы `/uslugi/{город}/{узел}/`.
 */
const cityNodeSlug = computed(() => paramToString(route.params.node) ? paramToString(route.params.segment) : '');
const routeCitySlug = computed(() => cityNodeSlug.value || segmentKind.value?.city || '');
let pendingCityStoreSlug: string | null = null;

async function syncConfirmedCityFromStore(): Promise<boolean> {
  const citySlug = cityStore.citySlug;

  if (
      !import.meta.client
      || cityStore.status !== 'chosen'
      || !citySlug
      || routeCitySlug.value
      || isGeoCatalogCityFilterCleared()
      || filters.value.city === citySlug
  ) {
    return false;
  }

  if (nuxtApp.isHydrating) {
    await new Promise<void>((resolve) => {
      nuxtApp.hooks.hookOnce('app:suspense:resolve', () => resolve());
    });

    return syncConfirmedCityFromStore();
  }

  pendingCityStoreSlug = citySlug;
  filters.value.city = citySlug;

  return true;
}

watch(
    () => [cityStore.status, cityStore.citySlug] as const,
    () => void syncConfirmedCityFromStore(),
    {flush: 'post'},
);

function onPageChanged(newPage: number) {
  shouldScrollOnPageChange.value = true;
  page.value = newPage;
  router.push(generateUrl(filters.value as TClinicFilter, newPage));
}

const serviceRequest = useAsyncData<TServiceResponse>(
    `serviceData-${route.fullPath}-${filters.value.service}-${filters.value.animal}-${filters.value.city}`,
    async () => {
      const slug = filters.value.service;
      const url = slug ? `public/services/${slug}` : 'public/services/categories/';
      const animal = filters.value.animal;
      const query: Record<string, string> = {};

      // Строки прайса сужаются тем же видом, что и список клиник. Иначе под фильтром «Собаки»
      // в сетке остаётся «Кастрация кота», и её выбор приводит на пустую выдачу.
      if (slug && animal && animal !== defaultAnimal) {
        query.animal = animal;
      }

      const city = cityNodeSlug.value || filters.value.city;
      if (slug && city) {
        query.city = city;
      }

      return Object.keys(query).length ? apiQuery(url, 'GET', {query}) : apiQuery(url, 'GET');
    },
    {
      watch: [() => route.fullPath, () => filters.value.service, () => filters.value.animal, () => filters.value.city],
      immediate: true,
    },
);
const {data: serviceData, error: serviceError, refresh: refreshService, status: serviceStatus} = serviceRequest;
if (route.params.segment) await serviceRequest;

// Узел каталога, открытый как плоский URL `/uslugi/{slug}/`. Иерархия в пути не отражена,
// поэтому родитель и дети приходят из ответа API — на них держатся крошки и перелинковка.
const catalogNode = computed(() => {
  const data = serviceData.value?.data;

  if (!data || Array.isArray(data) || data.type !== 'service_category') return null;

  return data;
});

// Вид самого узла: у видового лендинга фильтр зафиксирован его собственным видом (ТЗ §5).
// Берём корзину `animalGroup`, а не слаг животного: слаг в справочнике по стендам разный
// (koshka/koshek), и сравнение с опциями фильтра держится именно на корзине.
const landingAnimalSlug = computed<string>(() => {
  const node = catalogNode.value;

  if (!node?.animal) return '';

  return normalizeServiceCatalogAnimalSlug(node.animalGroup || node.animal.slug);
});

const isAnimalLanding = computed(() => !!landingAnimalSlug.value);

/**
 * Вид, по которому реально фильтруется выдача. У видового лендинга его задаёт сам адрес, и
 * параметр `?animal` там не выбирает ничего — он способен только расплодить дубли страницы,
 * поэтому в запрос не попадает.
 */
const effectiveAnimalSlug = computed<string>(
    () => isAnimalLanding.value ? landingAnimalSlug.value : (filters.value.animal || defaultAnimal),
);

// Несуществующая услуга в URL → 301 на каталог (город или корень).
watch(
    serviceError as Ref<{ statusCode?: number } | null | undefined>,
    (err) => {
      if (err?.statusCode !== 404 || !filters.value.service) {
        return;
      }

      const city = filters.value.city?.trim();
      const target = city ? `/uslugi/${encodeURIComponent(city)}/` : '/uslugi/';

      navigateTo(target, {redirectCode: 301, replace: true});
    },
    {immediate: true},
);

// Городской адрес живёт, только если сервер прямо подтвердил публикацию пары. Всё остальное —
// каноника, видовой лендинг, устаревший ответ без поля — уходит 302 на плоский адрес того же
// сегмента, где его разберут карта переезда и обычный роут. Проверять на «ниже порога» нельзя:
// у каноники поля нет вовсе, и такая страница осталась бы индексируемой выдачей по городу.
//
// Не 301: порог пересекается в обе стороны при суточном пересчёте, и постоянный редирект
// пришлось бы отзывать у робота вручную.
watch(
    serviceData,
    () => {
      if (!cityNodeSlug.value) {
        return;
      }

      const data = serviceData.value?.data;

      // Ответа нет — это 404, и им занимается 301 выше.
      if (!data || Array.isArray(data)) {
        return;
      }

      if (data.type === 'service_category' && data.cityPublished === true) {
        return;
      }

      const search = route.fullPath.split('?')[1] ?? '';
      const target = `/uslugi/${encodeURIComponent(paramToString(route.params.node))}/`;

      navigateTo(search ? `${target}?${search}` : target, {redirectCode: 302, replace: true});
    },
    {immediate: true},
);

const ensureServiceDataReady = async () => {
  if (!filters.value.service) return;
  if (serviceData.value?.data) return;

  if (serviceStatus.value === 'pending') {
    await new Promise<void>((resolve) => {
      const stop = watch(serviceStatus, (status) => {
        if (status !== 'pending') {
          stop();
          resolve();
        }
      });
    });
  }

  if (!serviceData.value?.data) {
    await refreshService();
  }
};

// Строка прайса после переезда каталога своего URL не имеет: `priceRow` сужает выдачу, не меняя
// саму страницу, и пишется саджестом поиска — собственного элемента выбора у него нет.
// Отдельный от `service` ключ нужен потому, что `service` держит идентичность страницы,
// а на корне `/uslugi/?service=` он ещё и означает выбор страницы услуги.
const selectedPriceRow = computed<string>(() => filters.value.priceRow || '');
const clinicServiceFilterKey = computed<'service_category' | 'service' | ''>(() => {
  const data = serviceData.value?.data;

  if (!filters.value.service || !data || Array.isArray(data) || !data.type) {
    return '';
  }

  return data.type === 'service_category' ? 'service_category' : 'service';
});

const clinicQuery = computed<TClinicFilter>(() => {
  const queryParams: Record<string, any> = {
    animal: effectiveAnimalSlug.value,
    city: filters.value.city || '',
    // `service_search`, а не `search`: `search` на бэке сравнивается только с названием клиники,
    // и запрос вроде «стерилизация» отдавал пустую выдачу при полном каталоге таких услуг.
    service_search: filters.value.search || '',
    isAlwaysOpen: filters.value.isAlwaysOpen
  };

  if (filters.value.service && clinicServiceFilterKey.value) {
    queryParams[clinicServiceFilterKey.value] = filters.value.service;
  }

  if (selectedPriceRow.value) {
    queryParams.service = selectedPriceRow.value;
  }

  return queryParams as TClinicFilter;
});

const emptyClinicPayload = (): {
  data: TClinic[];
  meta: Meta;
  filters: any;
  seo: FilterSeo;
} => ({
  data: [],
  meta: {
    currentPage: 1,
    perPage: 20,
    total: 0,
    lastPage: 1,
    from: 0,
    to: 0,
  },
  filters: null,
  seo: {} as FilterSeo,
});

const clinicsRequest = useAsyncData<{
  data: TClinic[];
  meta: Meta;
  filters: any;
  seo: FilterSeo;
}>(
    `clinics-${route.fullPath}-${JSON.stringify(clinicQuery.value)}-${page.value}-${ambiguousReady.value}`,
    async () => {
      if (!ambiguousReady.value) {
        return emptyClinicPayload();
      }
      await ensureServiceDataReady();
      const response = await apiQuery<{
        data: TClinicRaw[];
        meta: Meta;
        filters: any;
        seo: FilterSeo;
      }>('public/clinics', 'GET', {
        query: {...clinicQuery.value, page: page.value, pageType: 'service'},
      });
      return {...response, data: omitClinicPhoneFromList(response.data)};
    },
    {
      watch: [clinicQuery, () => page.value, ambiguousReady],
      immediate: true,
    },
);
const {data: clinicData, error: clinicError, refresh: refreshClinics, pending: clinicsPending} = clinicsRequest;
await Promise.all([serviceRequest, clinicsRequest]);

// Несуществующий город/услуга в ЧПУ → 404 вместо пустого списка клиник.
useThrowOnNotFound(clinicError as Ref<{ statusCode?: number } | null | undefined>);

watch(
    () => [route.params, route.query, segmentKind.value, segmentResolvePending.value],
    async () => {
      applyRouteToFilters();
      page.value = Number(route.query.page) || 1;
      viewType.value = route.query.view === 'map' ? 'map' : 'list';
      if (await syncConfirmedCityFromStore()) {
        return;
      }
      await refreshService();
      await refreshClinics();
    },
    {deep: true},
);

// Кнопка «Повторить» обязана дёрнуть оба запроса: ensureServiceDataReady() коротит на
// закешированном serviceData, поэтому один refreshClinics() не переспросил бы упавшую услугу.
const onRetryListing = async () => {
  await refreshService();
  await refreshClinics();
};

const clinics = computed<TClinic[]>(() => clinicData.value?.data || []);
const meta = computed<Meta | null>(() => clinicData.value?.meta || null);
const isClinicsLoading = computed(() => clinicsPending.value || loading.value);
const isEmptyResults = computed(() => !isClinicsLoading.value && !clinicError.value && !serviceError.value && !clinics.value.length);
const allClinicsUrl = computed(() => filters.value.city ? `/clinics/${filters.value.city}/` : '/clinics/');
const catalogRoot = ref<HTMLElement | null>(null);

const focusSearch = () => {
  const input = catalogRoot.value?.querySelector<HTMLInputElement>('.main-filter__search input');
  input?.focus();
  input?.select();
};

// Пагинация меняет только query, а scrollBehavior Nuxt для одинакового path скролл не делает.
watch(clinicsPending, (pending, wasPending) => {
  if (pending || !wasPending || !shouldScrollOnPageChange.value) return;

  shouldScrollOnPageChange.value = false;
  nextTick(() => {
    if (clinicList.value) {
      scrollPageToView(clinicList.value);
    }
  });
});


const currentServiceId = computed(() => {
  const serviceSlug = filters.value.service || '';
  if (!serviceSlug) return null;
  const data = serviceData.value?.data;
  if (data && !Array.isArray(data) && data.type === 'service' && data.slug === serviceSlug) {
    return data.id;
  }
  return null;
})

const generateUrl = (filters: TClinicFilter, pageNew?: number): string => {

  const queryParams = new URLSearchParams();
  // Иначе смена любого другого фильтра прогонит URL через генератор и молча снимет выбор строки.
  if (filters.priceRow) {
    queryParams.append('priceRow', filters.priceRow);
  }
  if (filters.isAlwaysOpen) {
    queryParams.append('isAlwaysOpen', 'y');
  }
  if (viewType.value !== 'list') queryParams.append('view', viewType.value);
  if (pageNew === undefined) {
    pageNew = page.value;
  }
  if (pageNew !== 1) queryParams.append('page', pageNew.toString());
  if (!filters.city) {
    if (filters.service && (filters.service || '').trim()) {
      let path = `/uslugi/${encodeURIComponent(filters.service || '')}/`;
      if (filters.animal && (filters.animal || '') !== defaultAnimal && (filters.animal || '').trim()) {
        queryParams.append('animal', filters.animal || '');
      }
      if (filters.search && (filters.search || '').trim()) {
        queryParams.append('search', filters.search || '');
      }
      const qs = queryParams.toString();
      return qs ? `${path}?${qs}` : path;
    }
    if (filters.animal && (filters.animal || '') !== defaultAnimal && (filters.animal || '').trim()) {
      queryParams.append('animal', filters.animal || '');
    }
    if (filters.search && (filters.search || '').trim()) {
      queryParams.append('search', filters.search || '');
    }
    const rootQs = queryParams.toString();
    return rootQs ? `/uslugi/?${rootQs}` : `/uslugi/`;
  }
  let path = `/uslugi/${encodeURIComponent(filters.city || '')}/`;

  if (filters.service) {
    // Узел, а не строка прайса с видом: `{город}/{услуга}-{вид}` — прежний адрес, который
    // legacy-catalog-redirect уводит 301 на плоский узел, теряя выбранный город.
    path += `${encodeURIComponent(filters.service)}/`;

    if (filters.animal && (filters.animal || '') !== defaultAnimal && (filters.animal || '').trim()) {
      queryParams.append('animal', filters.animal || '');
    }
  }

  if (filters.search && (filters.search || '').trim()) {
    queryParams.append('search', filters.search || '');
  }

  const queryString = queryParams.toString();
  return queryString ? `${path}?${queryString}` : path;
};

const paginationHref = (pageNum: number) => generateUrl(filters.value, pageNum);

let isUpdating = false;
watch(filters, async (newFilters, oldFilters) => {
  const isCityStoreSync = pendingCityStoreSlug === newFilters.city;
  pendingCityStoreSlug = null;

  if (newFilters.city) {
    clearGeoCatalogCityFilterCleared()
  } else if (oldFilters?.city) {
    markGeoCatalogCityFilterCleared()
  }
  if (isCityStoreSync || window.innerWidth <= 768) return;

  filterHandler();
}, {deep: true});

const formatServiceName = (slug: string) => {
  return slug.split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
};

const getServiceLabelFromFilters = (slug: string) => {
  const serviceFilters = clinicData.value?.filters?.service;
  if (!Array.isArray(serviceFilters)) return '';

  for (const group of serviceFilters) {
    if (group?.slug === slug && group?.label) {
      return group.label;
    }

    if (Array.isArray(group?.services)) {
      const service = group.services.find((item: any) => item?.slug === slug);
      if (service?.label) {
        return service.label;
      }
    }
  }

  return '';
};

const getPriceRowNodeSlug = (slug: string): string => {
  const serviceFilters = clinicData.value?.filters?.service;
  if (!Array.isArray(serviceFilters)) return '';

  for (const group of serviceFilters) {
    if (Array.isArray(group?.services) && group.services.some((item: any) => item?.slug === slug)) {
      return group?.slug || '';
    }
  }

  return '';
};

// Каноника собственного адреса не имеет, но живёт на странице своего узла: выбранная из
// списка чужая строка переносит на её узел, иначе крошки и сетка остаются от прежней страницы.
const followPriceRowToItsNode = (): void => {
  const priceRow = filters.value.priceRow;
  if (!priceRow) return;

  const node = getPriceRowNodeSlug(priceRow);
  if (node && node !== filters.value.service) {
    filters.value.service = node;
  }
};

const filterHandler = async () => {
  if (isUpdating) return;
  isUpdating = true;
  loading.value = true;

  try {
    followPriceRowToItsNode();
    if (page.value !== 1) page.value = 1;
    await refreshService();
    await refreshClinics();
    const newUrl = generateUrl(filters.value as TClinicFilter);
    if (route.fullPath !== newUrl) {
      await router.push(newUrl);
    }
  } catch (error) {

  } finally {
    isUpdating = false;
    loading.value = false;
    document.body.classList.remove('no-scroll');
  }
}

const updateOnMobile = () => {
  if (window.innerWidth > 768) return;
  filterHandler();
}

const clinicCount = computed(() => {
  return meta.value
      ? `${meta.value.total} ${declineWord(meta.value.total, ["клиника", "клиники", "клиник"])}`
      : "";
});

const categories = computed<ServiceCategory[]>(() => {
  if (serviceData.value?.data) {
    const data = serviceData.value.data;
    if (Array.isArray(data)) {
      return data as ServiceCategory[];
    }
  }

  return [];
});

const currentServiceName = computed(() => {
  const data = serviceData.value?.data;
  if (!data || Array.isArray(data)) {
    return '';
  }

  if (data.type === 'service' || data.type === 'service_category') {
    return data.name || '';
  }

  return '';
});


/* SEO — первый сегмент URL в трёхчастном пути может быть городом или slug категории */
const locationSegmentIsCity = computed(() => {
  const slug = filters.value.city;
  if (!slug || !clinicData.value?.filters?.city?.length) return false;
  return clinicData.value.filters.city.some((c: any) => c.slug === slug);
});

const firstSegmentCategoryLabel = computed(() => {
  const slug = filters.value.city;
  if (!slug || locationSegmentIsCity.value) return '';
  const groups = clinicData.value?.filters?.service;
  if (!Array.isArray(groups)) return '';
  const g = groups.find((x: any) => x?.slug === slug);
  return g?.label ? String(g.label) : '';
});

const locationSegmentIsServiceCategory = computed(() => !!firstSegmentCategoryLabel.value);

/* SEO */
const getCityLabel = (slug?: string) => {
  if (!slug) {
    slug = filters.value.city || '';
  }
  const safeSlug = slug || '';
  if (!safeSlug || !clinicData.value?.filters?.city) return 'г. ' + decodeURIComponent(safeSlug);
  const city = clinicData.value.filters.city.find((c: any) => c.slug === safeSlug);
  return city?.nameCases?.prepositional_singular || 'г.' + (city?.label || decodeURIComponent(safeSlug));
};

const getCityNameNominative = (slug?: string) => {
  const safeSlug = slug || filters.value.city || '';
  if (!safeSlug || !clinicData.value?.filters?.city) return decodeURIComponent(safeSlug);
  const city = clinicData.value.filters.city.find((c: any) => c.slug === safeSlug);
  return city?.label || decodeURIComponent(safeSlug);
};

const getServiceLabel = (slug: string) => {
  const data = serviceData.value?.data;
  if (data && !Array.isArray(data) && data.slug === slug) {
    if (data.type === 'service' || data.type === 'service_category') {
      return data.nameSeo || data.name;
    }
  }

  const filterLabel = getServiceLabelFromFilters(slug);
  if (filterLabel) return filterLabel;

  return formatServiceName(slug);
};

const getAnimalLabel = (slug: string) => {
  // Вид дописывается к заголовку, только когда он выбран: на «Все» узел зовётся своим именем.
  // «для животных» здесь запрещено в любом случае — формулировка с видами собирает на порядок
  // больше спроса, поэтому обезличенного варианта в H1 и Title не появляется.
  if (!slug || slug === 'zhivotnyh') return '';
  if (!clinicData.value?.filters?.animal) return '';
  const animal = clinicData.value.filters.animal.find((a: any) => a.slug === slug);
  return animal?.seoName || animal?.label || '';
};

// Заголовок уже называет вид животного, дописывать его нельзя — иначе выходит
// «Стерилизация кошки для собак и кошек». Два случая: nameSeo из спеки (готовый H1) и видовой
// лендинг, у которого вид зашит в само название узла.
const labelAlreadyNamesSpecies = (slug: string) => {
  const data = serviceData.value?.data;

  if (!data || Array.isArray(data) || data.slug !== slug) return false;
  if (data.type !== 'service' && data.type !== 'service_category') return false;

  return !!(data.nameSeo || (data.type === 'service_category' && data.animal));
};

const getPageServiceLabel = (slug: string) => {
  const serviceLabel = getServiceLabel(slug);

  if (labelAlreadyNamesSpecies(slug)) {
    return serviceLabel;
  }

  const animalLabel = getAnimalLabel(filters.value.animal || defaultAnimal);
  if (!animalLabel) return serviceLabel;
  if (serviceLabel.toLowerCase().endsWith(animalLabel.toLowerCase())) {
    return serviceLabel;
  }
  return `${serviceLabel} ${animalLabel}`;
};

const catalogParent = computed(() => catalogNode.value?.parent ?? null);

const animalOptions = computed<AnimalOption[]>(() => {
  const options = (clinicData.value?.filters?.animal ?? []) as AnimalOption[];

  if (!isAnimalLanding.value) {
    return options;
  }

  // На лендинге выбор приходит из самого адреса, а не из параметра запроса, поэтому отметку
  // «выбрано» приходится расставлять здесь.
  return options.map((option) => ({...option, selected: option.slug === landingAnimalSlug.value}));
});

/**
 * Виды, у которых есть собственный лендинг: их опции — ссылки, а не состояние страницы.
 * Источник — animalPages, а не children: на лендинге детей нет, и без отдельного поля соседние
 * виды были бы фронту неизвестны — оттуда и брался тупик «выбрал собаку и не могу выбрать кошку».
 */
const animalLandings = computed<Record<string, string>>(() => {
  const map: Record<string, string> = {};

  for (const page of catalogNode.value?.animalPages ?? []) {
    if (page.animalGroup) {
      map[page.animalGroup] = page.slug;
    }
  }

  return map;
});

const selectedAnimalLabel = computed(() => {
  if (clinics.value.length || isClinicsLoading.value) return '';

  const selected = animalOptions.value.find((option) => option.selected && option.slug !== defaultAnimal);

  return selected ? (selected.seoName || selected.label.toLowerCase()) : '';
});

const onAnimalSelect = async (slug: string) => {
  const cityParentSlug = filters.value.city && isAnimalLanding.value
      ? catalogParent.value?.slug
      : '';

  if (filters.value.animal === slug && !cityParentSlug) return;

  if (cityParentSlug) filters.value.service = cityParentSlug;
  filters.value.animal = slug;
  await filterHandler();
};

const catalogChildren = computed(() => catalogNode.value?.children ?? []);

/**
 * Плитками показываются только подстраницы: у них есть собственный адрес, и плитка честно
 * обещает переход (ТЗ §7.3). Видовые лендинги отсюда исключены — они живут строкой «Питомец».
 */
const childTiles = computed(() => catalogChildren.value
    .filter((child) => !child.animalGroup)
    .map((child) => ({
      id: child.id,
      slug: child.slug,
      name: child.nameSeo || child.name,
      clinicsCount: child.clinicsCount,
    })));

const pageBreadcrumbs = computed(() => {
  const list = [{name: "Ветеринарные услуги", link: "/uslugi/"}];

  // Подстраница и видовой лендинг: единственное место, где видна принадлежность хабу.
  if (!filters.value.city && catalogParent.value) {
    list.push({
      name: catalogParent.value.nameSeo || catalogParent.value.name,
      link: `/uslugi/${catalogParent.value.slug}/`,
    });
  }

  if (filters.value.city) {
    const midName = locationSegmentIsCity.value
        ? getCityNameNominative(filters.value.city)
        : (locationSegmentIsServiceCategory.value
            ? `Категория «${firstSegmentCategoryLabel.value}»`
            : decodeURIComponent(filters.value.city));
    list.push({
      name: midName,
      link: `/uslugi/${filters.value.city}/`
    });

    if (filters.value.service) {
      const serviceLabel = getPageServiceLabel(filters.value.service);
      list.push({
        name: serviceLabel,
        link: generateUrl(filters.value as TClinicFilter)
      });
    }
  } else if (filters.value.service) {
    const serviceLabel = getPageServiceLabel(filters.value.service);
    list.push({
      name: serviceLabel,
      link: generateUrl(filters.value as TClinicFilter)
    });
  }

  return list;
});

const getPageTitle = computed(() => {
  if (!filters.value.city) {
    // Узел каталога открывается без города: раньше у страницы услуги город в пути был всегда,
    // и ветка «без города» означала корень /uslugi/. Теперь без неё все 53 страницы каталога
    // отдавали бы один и тот же обезличенный Title.
    if (filters.value.service) {
      return `${getPageServiceLabel(filters.value.service)} — цены, клиники, отзывы`;
    }

    return 'Ветеринарные услуги';
  }

  if (filters.value.service) {
    const serviceLabel = getPageServiceLabel(filters.value.service || '');
    if (locationSegmentIsCity.value) {
      const cityLabel = getCityLabel(filters.value.city);
      return `${serviceLabel} в ${cityLabel} — запись, цены, отзывы`;
    }
    if (locationSegmentIsServiceCategory.value) {
      return `${serviceLabel} — ${firstSegmentCategoryLabel.value} | Цены и отзывы на Vetsy`;
    }
    return `${serviceLabel} | Цены и отзывы на Vetsy`;
  }

  if (locationSegmentIsCity.value) {
    return `Ветеринарные услуги в ${getCityLabel(filters.value.city)} — запись, цены, отзывы`;
  }
  if (locationSegmentIsServiceCategory.value) {
    return `Услуги ветеринарных клиник — ${firstSegmentCategoryLabel.value} | Vetsy.ru`;
  }
  return 'Ветеринарные услуги | Vetsy.ru';
});

const clinicsCountPhrase = computed(() => {
  const total = meta.value?.total ?? 0;
  return `${total} ${declineWord(total, ['клиника', 'клиники', 'клиник'])}`;
});

const getPageDescription = computed(() => {
  if (!filters.value.city) {
    if (filters.value.service) {
      return `${getPageServiceLabel(filters.value.service)}: ${clinicsCountPhrase.value} с онлайн-записью. `
          + 'Актуальные цены, адреса и реальные отзывы владельцев.';
    }

    return 'Запишитесь на прием в ветеринарную клинику онлайн ☎️ Адреса, время работы ветклиник, а также подлинные отзывы от посетителей.';
  }

  const cityLabel = getCityLabel(filters.value.city || '');
  const catLabel = firstSegmentCategoryLabel.value;
  const stats = (clinicData.value as any)?.serviceStats;
  const minPrice = stats?.min_price && stats.min_price !== '0' ? stats.min_price : '';
  const avgRating = stats?.avg_rating && stats.avg_rating !== '0' ? stats.avg_rating : '';
  const pricePart = minPrice ? `, цены от ${minPrice} ₽` : '';
  const ratingPart = avgRating ? `, рейтинг ${avgRating}` : '';

  if (!filters.value.service) {
    if (locationSegmentIsCity.value) {
      return `Найти и записаться на ветеринарные услуги в ${cityLabel}: ${clinicsCountPhrase.value}${pricePart}${ratingPart}. Онлайн-запись, реальные отзывы.`;
    }
    if (locationSegmentIsServiceCategory.value) {
      return `Запишитесь на ветеринарные услуги в категории «${catLabel}» в любой из ${clinicsCountPhrase.value} онлайн ☎️ Только актуальные цены и проверенные отзывы.`;
    }
    return `Запишитесь на ветеринарные услуги онлайн ☎️ ${clinicsCountPhrase.value} — актуальные цены и отзывы.`;
  }

  const serviceLabel = getPageServiceLabel(filters.value.service || '');

  if (locationSegmentIsCity.value) {
    return `Найти и записаться на ${serviceLabel} в ${cityLabel}: ${clinicsCountPhrase.value}${pricePart}${ratingPart}. Онлайн-запись, реальные отзывы.`;
  }
  if (locationSegmentIsServiceCategory.value) {
    return `Выберите клинику и запишитесь на ${serviceLabel} в категории «${catLabel}» (${clinicsCountPhrase.value}) онлайн ☎️ Актуальные цены и отзывы.`;
  }
  return `Выберите клинику и запишитесь на ${serviceLabel} онлайн ☎️ ${clinicsCountPhrase.value} — цены и отзывы.`;
});

const serviceSeoRobots = computed(() => {
  // Неопубликованный узел каталога (нет активных услуг) обязан остаться резолвимым — иначе его
  // слаг провалится в city-fallback и отдаст 200 с чужим контентом, — но не должен индексироваться.
  if (catalogNode.value && catalogNode.value.isPublished === false) {
    return 'noindex, follow'
  }

  if (meta.value?.total === 0) {
    return 'noindex, follow'
  }
  // Выбор строки прайса — фильтр, а не отдельная страница: плодить индексируемые адреса
  // произведением категорий на строки прайса и был смысл переезда убрать.
  if (selectedPriceRow.value) {
    return 'noindex, follow'
  }
  // Вид животного — тоже фильтр: индексируемая страница вида — это лендинг, а не адрес с
  // параметром (ТЗ §5). Сам параметр в canonical не попадает — тот собирается из route.path.
  // На лендинге параметр выдачу уже не меняет, но адрес с ним — тот же дубль, и в индекс он
  // попадать не должен.
  if (isAnimalLanding.value ? !!route.query.animal : (filters.value.animal && filters.value.animal !== defaultAnimal)) {
    return 'noindex, follow'
  }
  if (clinicData.value?.seo?.robots) {
    return clinicData.value.seo.robots
  }
  return undefined
})

useSeoMeta({
  robots: () => serviceSeoRobots.value,
})

useHead({
  title: () => clinicData.value?.seo?.metaTitle || getPageTitle.value,
  meta: [
    {
      name: 'description',
      content: () => clinicData.value?.seo?.metaDescription || getPageDescription.value
    },
    {
      property: 'og:title',
      content: () => clinicData.value?.seo?.metaTitle || getPageTitle.value
    },
    {
      property: 'og:description',
      content: () => clinicData.value?.seo?.metaDescription || getPageDescription.value
    }
  ]
});

const pageTitle = computed(() => {
  if (clinicData.value?.seo?.title) return clinicData.value.seo.title;

  if (!filters.value.city) {
    // H1 страницы каталога. Без этой ветки узел отдавал бы «Оказание ветеринарных услуг»
    // — одинаковый заголовок на всех страницах и провал проверки «слаг рендерит свою страницу».
    if (filters.value.service) {
      return getPageServiceLabel(filters.value.service);
    }

    return 'Оказание ветеринарных услуг';
  }

  const cityLabel = getCityLabel(filters.value.city);
  const catTitle = firstSegmentCategoryLabel.value;

  if (filters.value.service) {
    const serviceLabel = getPageServiceLabel(filters.value.service || '');
    if (locationSegmentIsCity.value) {
      return `${serviceLabel} в ${cityLabel}`;
    }
    if (locationSegmentIsServiceCategory.value) {
      return `${serviceLabel} — ${catTitle}`;
    }
    return serviceLabel;
  }

  if (locationSegmentIsCity.value) {
    return `Ветеринарные услуги в ${cityLabel}`;
  }
  if (locationSegmentIsServiceCategory.value) {
    return `Ветеринарные услуги — ${catTitle}`;
  }
  return 'Оказание ветеринарных услуг';
});

const getServiceDescription = computed(() => {
  if (clinicData.value?.seo?.subtitle) return clinicData.value.seo.subtitle;

  const data = serviceData.value?.data;
  if (data && !Array.isArray(data)) {
    if (data.type === 'service' || data.type === 'service_category') {
      return data.description || '';
    }
  }
  return '';
});

// URL каталога плоский всегда: города и вида в пути больше нет, у строки прайса своего адреса
// не осталось. Прежняя форма `/uslugi/{город}/{услуга}-{вид}/` теперь только источник 301.
const getRelatedServiceUrl = (slug: string): string => `/uslugi/${encodeURIComponent(slug)}/`;

function onUpdateFilters(updatedFilters: Record<string, any>) {
  updateFiltersData(clinicData, updatedFilters);
}
</script>

<style lang="scss">
.clinics {
  display: flex;
  flex-direction: column;
  gap: 12px;

  & .button {
    @include media(sm) {
      width: 100%;
    }
  }

}

.main-filter__bottom._desktop.main-filter__bottom--service {
  display: flex;
  bottom: 0;
  position: relative;
  margin-top: 16px;
  align-items: center;
  justify-content: space-between;
  flex-wrap: nowrap;

  & .main-filter__clear {
    margin-top: 0;
  }
}

.service-child-link {
  display: inline-flex;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 32px;
  padding: 12px 20px;
  border-radius: 16px;
  background: $color-white;
  color: $color-black;
  font-size: 16px;
  text-decoration: none;

  &__count {
    color: $color-accent-text;
    font-size: 14px;
  }

  &:hover {
    background: $color-dark-white;
  }
}

.service-related {
  margin-top: 40px;

  &__title {
    font-size: 32px;
    font-weight: 500;
    margin-bottom: 24px;
    color: $color-black;

    @include media(sm) {
      font-size: 24px;
      margin-bottom: 20px;
    }
  }

  &__list {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__item {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 16px;
    line-height: 22px;
    color: $color-accent-text;
    text-decoration: none;
    transition: opacity 0.2s ease;

    &:hover {
      opacity: 0.7;
    }

    @include media(sm) {
      font-size: 14px;
      line-height: 19px;
    }
  }

  &__icon {
    flex-shrink: 0;
    width: 20px;
    height: 20px;
    color: $color-accent-text;
  }
}
</style>
